'use client';

import React, { useEffect, useRef, useState } from 'react';
import styles from './CurrencyConverter.module.scss';
import CurrencySelector from '../CurrencyConverter/currencySelector/currencySelector';
import getCurrencySymbol from '../CurrencyConverter/utils/getCurrencySymbol';
import sanitiseCurrencyInput from '../CurrencyConverter/utils/sanitiseCurrencyInput';
import currency from 'currency.js';
import Cta from '@JohnLewisPartnership/jlfs-ui-library/dist/components/cta/cta';
import { MappedFresCurrency } from '../../utils/getFresCurrencies/getFresCurrencies';
import { FresQuoteResult } from '../../app/api/fres-quote/getFresQuoteData';
import { useDebounce } from '../../hooks/useDebounce/useDebounce';
import { PrimaryButton } from '@JohnLewisPartnership/jl-design-system/dist/elements/button/Button';
import { dispatchAnalyticsEvent } from '@JohnLewisPartnership/jlfs-ui-library/dist/components/analytics/Analytics';
import Message from '@JohnLewisPartnership/jlfs-ui-library/dist/components/message/message';
import { LoadingSpinner } from '@JohnLewisPartnership/jl-design-system/dist/components/loading-spinner/LoadingSpinner';
import { trackMonetateEvent } from '@JohnLewisPartnership/jlfs-ui-library/dist/utils/monetate/trackMonetateEvent';
import Icon from '@JohnLewisPartnership/jlfs-ui-library/dist/components/icon/icon';
import PaymentCards from '../PaymentCards/PaymentCards';
import classNames from 'classnames';
import buildSafeFresUrl from '../../utils/buildSafeFresUrl/buildSafeFresUrl';

export type TCurrencyConverterCCProps = {
  title: string;
  ctaLinkToSectionId?: string;
  enableRateSalePageCta?: boolean;
  currencyList: Array<MappedFresCurrency>;
  rateError?: boolean;
  fresUrl: string;
  variant?: string;
  monetateTrackingId?: string;
  monetateInteractionId?: string;
  monetateViewRatesId?: string;
};

const gbpCurrency: MappedFresCurrency = {
  countryCode: 'GB',
  currencyCode: 'GBP',
  currencyName: 'Great British Pounds',
  currencySymbol: '£',
  productId: undefined,
};

const extractCurrencyValue = (
  value: string,
  symbol: string
): string | undefined => {
  const parts = value.split(symbol);
  return parts.length > 1 ? parts[1] : undefined;
};

const MAX_VALUE = 1000000000;

const formatPounds = (value: number): string =>
  `£${currency(value).format({ symbol: '' })}`;

const formatSelectedCurrency = (symbol: string, value: number): string =>
  `${symbol}${currency(value).format({ symbol: '' })}`;

function CurrencyConverterCC({
  title,
  ctaLinkToSectionId = '',
  enableRateSalePageCta = false,
  currencyList,
  rateError,
  fresUrl,
  variant,
  monetateTrackingId,
  monetateInteractionId,
  monetateViewRatesId,
}: TCurrencyConverterCCProps) {
  const safeFresUrl = buildSafeFresUrl(fresUrl);

  const defaultSelectedCurrency = currencyList[0];
  const defaultProductId =
    defaultSelectedCurrency?.productId ?? defaultSelectedCurrency?.currencyCode;
  const [selectedCurrency, setSelectedCurrency] = useState<MappedFresCurrency>(
    defaultSelectedCurrency
  );
  const [selectedCurrencySymbol, setSelectedCurrencySymbol] = useState<string>(
    getCurrencySymbol(selectedCurrency?.currencyCode)
  );
  const [poundsInputValue, setPoundsInputValue] = useState<string>('£');
  const [selectedCurrencyInputValue, setSelectedCurrencyInputValue] =
    useState<string>(selectedCurrencySymbol);
  const [quoteId, setQuoteId] = useState<string | undefined>(undefined);
  const [exchangeRate, setExchangeRate] = useState<number | undefined>(
    undefined
  );
  const [isRateLoading, setIsRateLoading] = useState<boolean>(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState<boolean>(false);
  const [apiSummaryValues, setApiSummaryValues] = useState<
    { pounds: string; foreign: string } | undefined
  >(undefined);
  const [apiError, setApiError] = useState<boolean>(false);
  const noValueEntered =
    poundsInputValue === '£' &&
    selectedCurrencyInputValue === selectedCurrencySymbol;
  const abortControllerRef = useRef<AbortController | null>(null);
  const rateRequestCountRef = useRef<number>(0);
  const summaryRequestCountRef = useRef<number>(0);
  const summaryLoadingStartRef = useRef<number>(0);
  const summaryLoadingTimerRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  const startRateLoading = () => {
    rateRequestCountRef.current += 1;
    setIsRateLoading(true);
  };

  const stopRateLoading = () => {
    rateRequestCountRef.current = Math.max(0, rateRequestCountRef.current - 1);
    if (rateRequestCountRef.current === 0) {
      setIsRateLoading(false);
    }
  };

  const startSummaryLoading = () => {
    summaryRequestCountRef.current += 1;
    summaryLoadingStartRef.current = Date.now();
    if (summaryLoadingTimerRef.current) {
      clearTimeout(summaryLoadingTimerRef.current);
      summaryLoadingTimerRef.current = undefined;
    }
    setIsSummaryLoading(true);
  };

  const stopSummaryLoading = (isError: boolean = false) => {
    summaryRequestCountRef.current = Math.max(
      0,
      summaryRequestCountRef.current - 1
    );
    if (summaryRequestCountRef.current === 0) {
      if (isError) {
        setIsSummaryLoading(false);
      } else {
        const elapsed = Date.now() - summaryLoadingStartRef.current;
        const remaining = 1000 - elapsed;
        if (remaining > 0) {
          summaryLoadingTimerRef.current = setTimeout(() => {
            if (summaryRequestCountRef.current === 0) {
              setIsSummaryLoading(false);
            }
          }, remaining);
        } else {
          setIsSummaryLoading(false);
        }
      }
    }
  };

  const openNewTab = (e: React.MouseEvent) => {
    e.preventDefault();
    if (typeof window === 'undefined') return;

    const exchangeUrl = quoteId
      ? buildSafeFresUrl(fresUrl, quoteId)
      : safeFresUrl;

    if (!exchangeUrl) {
      return;
    }

    trackMonetateEvent(monetateTrackingId || 'FXOrderNowHeroClick');

    dispatchAnalyticsEvent('currencyConverterOrderNowClick', {
      linkUrl: exchangeUrl,
      linkText: 'Order now',
      currency: selectedCurrency?.currencyCode || 'EUR',
      currencyType: '',
      sourceAmount: extractCurrencyValue(poundsInputValue, '£'),
      destinationAmount:
        extractCurrencyValue(
          selectedCurrencyInputValue,
          selectedCurrencySymbol
        ) || '',
    });
    window.open(exchangeUrl, '_blank', 'noopener');
  };

  useEffect(() => {
    return () => {
      if (summaryLoadingTimerRef.current) {
        clearTimeout(summaryLoadingTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    dispatchAnalyticsEvent('fxCalculatorCTA', {
      calculatorAction: 'Currency Change',
      calculatorDetail: selectedCurrency?.currencyCode || 'EUR',
    });
  }, [selectedCurrency]);

  const callApi = async (
    productId: string,
    symbol: string,
    value: number,
    reverse: boolean,
    showRateLoading: boolean,
    currencyCode: string
  ) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    startSummaryLoading();
    if (showRateLoading) {
      startRateLoading();
    }
    setApiError(false);
    let hasError = false;
    try {
      const response = await fetch('/api/fres-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId,
          value: Math.min(value, MAX_VALUE),
          reverse,
        }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Failed to fetch FRES quote');
      const result: FresQuoteResult = await response.json();
      const poundsFormatted = formatPounds(result.poundsValue);
      const currencyFormatted = formatSelectedCurrency(
        symbol,
        result.currencyValue
      );
      setQuoteId(result.quoteId);
      setExchangeRate(result.exchangeRate);
      setApiSummaryValues({
        pounds: poundsFormatted,
        foreign: currencyFormatted,
      });

      // Keep entered values as user input and only use API rate for local conversion.
      if (result.exchangeRate > 0) {
        if (!reverse) {
          setSelectedCurrencyInputValue(
            formatSelectedCurrency(symbol, value * result.exchangeRate)
          );
        } else {
          setPoundsInputValue(formatPounds(value / result.exchangeRate));
        }
      }

      dispatchAnalyticsEvent('currencyConverterInteraction', {
        currency: currencyCode || 'EUR',
        currencyType: '',
        sourceAmount: extractCurrencyValue(poundsFormatted, '£'),
        destinationAmount: extractCurrencyValue(currencyFormatted, symbol),
      });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      hasError = true;
      setApiError(true);
      setQuoteId(undefined);
      setExchangeRate(undefined);
    } finally {
      stopSummaryLoading(hasError);
      if (showRateLoading) {
        stopRateLoading();
      }
    }
  };

  const fetchInitialRate = async (productId: string): Promise<void> => {
    if (!productId) {
      setIsRateLoading(false);
      return;
    }

    startRateLoading();
    try {
      const response = await fetch('/api/fres-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, value: 1, reverse: false }),
      });
      if (!response.ok) return;
      const result: FresQuoteResult = await response.json();
      if (result.exchangeRate > 0) {
        setExchangeRate(result.exchangeRate);
      }
    } catch {
      // silently ignore — rate will be populated once the user triggers a full quote
    } finally {
      stopRateLoading();
    }
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (defaultProductId && !rateError) {
      timeoutId = setTimeout(() => {
        fetchInitialRate(defaultProductId);
      }, 0);
    } else {
      setIsRateLoading(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const { reset: debouncedApiCall, cancel: cancelDebouncedApiCall } =
    useDebounce(callApi, 250);

  const handlePoundsInputValueUpdated = (value: string): void => {
    const sanitised = sanitiseCurrencyInput('£', value);
    setPoundsInputValue(sanitised);
    const numValue = currency(sanitised).value;
    if (numValue > 0) {
      if (exchangeRate !== undefined && exchangeRate > 0) {
        setSelectedCurrencyInputValue(
          formatSelectedCurrency(
            selectedCurrencySymbol,
            numValue * exchangeRate
          )
        );
      } else {
        setSelectedCurrencyInputValue(selectedCurrencySymbol);
      }
      abortControllerRef.current?.abort();
      debouncedApiCall(
        selectedCurrency.productId ?? selectedCurrency.currencyCode,
        selectedCurrencySymbol,
        numValue,
        false,
        exchangeRate === undefined,
        selectedCurrency.currencyCode
      );
    } else {
      cancelDebouncedApiCall();
      abortControllerRef.current?.abort();
      setSelectedCurrencyInputValue(selectedCurrencySymbol);
      setApiSummaryValues(undefined);
    }
  };

  const handleSelectedCurrencyInputValueUpdated = (value: string): void => {
    const sanitised = sanitiseCurrencyInput(selectedCurrencySymbol, value);
    setSelectedCurrencyInputValue(sanitised);
    const numValue = currency(
      sanitised.replace(selectedCurrencySymbol, '')
    ).value;
    if (numValue > 0) {
      if (exchangeRate !== undefined && exchangeRate > 0) {
        setPoundsInputValue(formatPounds(numValue / exchangeRate));
      } else {
        setPoundsInputValue('£');
      }
      abortControllerRef.current?.abort();
      debouncedApiCall(
        selectedCurrency.productId ?? selectedCurrency.currencyCode,
        selectedCurrencySymbol,
        numValue,
        true,
        exchangeRate === undefined,
        selectedCurrency.currencyCode
      );
    } else {
      cancelDebouncedApiCall();
      abortControllerRef.current?.abort();
      setPoundsInputValue('£');
      setApiSummaryValues(undefined);
    }
  };

  const handleNewCurrencySelected = (newCurrency: MappedFresCurrency): void => {
    setSelectedCurrency(newCurrency);
    const newSymbol = getCurrencySymbol(newCurrency.currencyCode);
    setSelectedCurrencySymbol(newSymbol);
    setSelectedCurrencyInputValue(newSymbol);
    setExchangeRate(undefined);
    setApiSummaryValues(undefined);
    const poundsValue = currency(poundsInputValue).value;
    const foreignValue = currency(
      selectedCurrencyInputValue.replace(selectedCurrencySymbol, '')
    ).value;
    if (poundsValue > 0) {
      callApi(
        newCurrency!.productId! ?? newCurrency!.currencyCode!,
        newSymbol,
        poundsValue,
        false,
        true,
        newCurrency.currencyCode ?? ''
      );
    } else if (foreignValue > 0) {
      callApi(
        newCurrency!.productId! ?? newCurrency!.currencyCode!,
        newSymbol,
        foreignValue,
        true,
        true,
        newCurrency.currencyCode ?? ''
      );
    } else {
      fetchInitialRate(newCurrency.productId ?? newCurrency.currencyCode ?? '');
    }
  };

  const handleInputFocusUpdated = (value: string): void => {
    if (value.includes('£')) {
      if (currency(value).value === 0) setPoundsInputValue('£');
    } else if (value.includes(selectedCurrencySymbol)) {
      if (currency(value).value === 0)
        setSelectedCurrencyInputValue(selectedCurrencySymbol);
    }
  };

  return (
    <section>
      <div
        className={styles.currencyConverter}
        data-testid="currency-converter"
      >
        <div className={styles.topSection}>
          <h2 className={styles.title}>{title}</h2>
          <span className={styles.subheading}>
            Minimum total order £250 - maximum £2,500
          </span>
        </div>
        {rateError && (
          <div className={styles.errorMessage}>
            <Message
              title="We're unable to display exchange rates at the moment."
              type="warning"
            >
              <span>
                Please try again later or view our{' '}
                <a href={safeFresUrl || '#'} target="_blank" rel="noopener">
                  order travel money page
                </a>
              </span>
            </Message>
          </div>
        )}
        <div className={styles.bottomSection}>
          {!rateError && (
            <div
              className={classNames(styles.bottomSectionContent, {
                [styles.apiError]: apiError,
              })}
            >
              <div className={styles.firstContainer}>
                <span className={classNames(styles.inputSection)}>
                  <span className={styles.inputContainer}>
                    <CurrencySelector
                      label="How much would you like?"
                      selectedCurrency={gbpCurrency}
                      inputValue={poundsInputValue}
                      dropdownEnabled={false}
                      disabled={apiError}
                      inputValueUpdated={handlePoundsInputValueUpdated}
                      focusUpdated={handleInputFocusUpdated}
                      monetateClickEvent={monetateInteractionId}
                    ></CurrencySelector>
                  </span>
                  <span className={styles.inputContainer}>
                    <CurrencySelector
                      label="What you’ll get in your currency"
                      currencyList={currencyList.filter(
                        (c) => c.currencyCode !== selectedCurrency.currencyCode
                      )}
                      selectedCurrency={selectedCurrency}
                      inputValue={selectedCurrencyInputValue}
                      disabled={apiError}
                      inputValueUpdated={
                        handleSelectedCurrencyInputValueUpdated
                      }
                      selectedCurrencyUpdated={handleNewCurrencySelected}
                      focusUpdated={handleInputFocusUpdated}
                      monetateClickEvent={monetateInteractionId}
                    ></CurrencySelector>
                  </span>
                </span>
              </div>
              <div className={styles.secondContainer}>
                <span className={styles.summaryTitle}>Order summary</span>
                <div className={styles.summaryContainer}>
                  {isSummaryLoading ? (
                    <div
                      className={classNames(
                        styles.currencyTitle,
                        styles.loading
                      )}
                    >
                      <LoadingSpinner
                        size="small"
                        className={styles.summaryLoadingSpinner}
                      />
                      <span className={styles.loadingText}>
                        Calculating amount
                      </span>
                    </div>
                  ) : noValueEntered || !apiSummaryValues ? (
                    <span
                      className={classNames(
                        styles.currencyTitle,
                        styles.placeholder
                      )}
                    >
                      {apiError
                        ? 'Order now to continue'
                        : 'Enter an amount to convert your currency'}
                    </span>
                  ) : (
                    <div className={styles.currencyTitle}>
                      <div
                        className={classNames(
                          styles.splitContainer,
                          styles.youPay
                        )}
                      >
                        <span className={styles.firstContainer}>
                          You&apos;ll pay
                        </span>
                        <span>{apiSummaryValues.pounds}</span>
                      </div>
                      <div
                        className={classNames(
                          styles.splitContainer,
                          styles.youGet
                        )}
                      >
                        <span className={styles.firstContainer}>
                          You&apos;ll get
                        </span>
                        <span className={styles.foreignValue}>
                          {apiSummaryValues.foreign}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className={styles.splitContainer}>
                    <span className={styles.firstContainer}>Online rate</span>
                    {apiError ? (
                      <span className={classNames(styles.placeholder)}>
                        Currently unavailable
                      </span>
                    ) : isRateLoading ? (
                      <span className={styles.onlineRateValueLoading}>
                        <LoadingSpinner
                          size="small"
                          className={styles.onlineRateLoadingSpinner}
                        />
                        <span>Loading rate</span>
                      </span>
                    ) : (
                      <span className={styles.onlineRateValue}>
                        {exchangeRate !== undefined && !rateError
                          ? `£1 = ${selectedCurrencySymbol}${exchangeRate} ${selectedCurrency.currencyCode}`
                          : `£1 = `}
                      </span>
                    )}
                  </div>
                  <div className={styles.rateInfoContainer}>
                    <Icon
                      className={styles.infoIcon}
                      name="info"
                      color="heritage-green"
                      size="medium"
                    />
                    <span className={styles.infoText}>
                      Amounts are rounded up or down to the nearest note
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {apiError && (
            <div className={styles.errorMessage}>
              <Message
                className={styles.content}
                title="Our currency calculator isn’t available at the moment"
                copy="You can still order now and choose your currency at the next step."
                type="warning"
                icon={null}
              />
            </div>
          )}
          <div
            className={classNames(
              variant === 'JLMFX004'
                ? styles.ctaContainerJLMFX004
                : styles.ctaContainer
            )}
          >
            {!rateError ? (
              <Cta
                className={styles.viewRates}
                type="underlined"
                target={
                  enableRateSalePageCta
                    ? '/travel-money/rate-table'
                    : ctaLinkToSectionId.charAt(0) === '#'
                      ? ctaLinkToSectionId
                      : `#${ctaLinkToSectionId}`
                }
                label="View currency rate table"
                monetateClickEvent={monetateViewRatesId}
              />
            ) : (
              <div></div>
            )}
            {variant === 'JLMFX004' && <PaymentCards theme="light" />}
            <PrimaryButton
              data-testid="primary-button"
              tag="a"
              href=""
              target="_blank"
              inline={true}
              rel="noopener"
              onClick={openNewTab}
              className={styles.orderNowButton}
              tabIndex={0}
            >
              Order now
            </PrimaryButton>
          </div>
        </div>
      </div>
    </section>
  );
}

export default CurrencyConverterCC;
